import BaseListPresenter from "../../../base/BaseListPresenter";
import * as XLSX from "xlsx";

class AnnualTaxesPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }
  init() {
    this.limit = 50;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }
  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  _getYearOptions() {
    const startYear = 2015;
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let year = currentYear; year >= startYear; year--) {
      years.push(year.toString());
    }
    return years;
  }

  _handleExportCsv(includeLabel, objects) {
    const { selectedYear } = this.view.state;
    const schema = this.view.getSchema(this.view.getCollectionName());
    const label = this._getExportLabels(schema, includeLabel);

    const processedData = objects.map((obj) => {
      const rowObj = { ...obj };
      const processedRow = {};

      label.forEach(({ key, label: headerLabel }) => {
        let val = rowObj[key];

        if (Array.isArray(val) && selectedYear && selectedYear !== "") {
          val = val.filter((item) =>
            item.toLowerCase().includes(selectedYear.toLowerCase())
          );
        }

        processedRow[headerLabel] =
          val == null ? "" : Array.isArray(val) ? val.join("\n") : String(val);
      });

      return processedRow;
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(processedData);

    // Set column widths
    const colWidths = label.map(({ label: header }) => {
      const maxWidth = Math.max(
        header.length,
        ...processedData.map((row) => (row[header] || "").length)
      );
      return { wch: Math.min(Math.max(maxWidth + 2, 10), 50) };
    });
    worksheet["!cols"] = colWidths;

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = {
          font: {
            bold: true,
          },
          alignment: {
            vertical: "center",
            horizontal: "center",
          },
        };
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Export Data");

    const fileName = `Employee_Tax_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName, { cellStyles: true });
  }

  _getExportLabels = (schema, includeLabel) => {
    return Object.keys(schema.fields)
      .filter((fieldName) => {
        const config = schema.fields[fieldName];
        return (
          config && config.read !== false && !includeLabel.includes(fieldName)
        );
      })
      .map((fieldName) => ({
        key: fieldName,
        label: schema.fields[fieldName].label || this._camelToLabel(fieldName),
      }));
  };

  _camelToLabel = (str) =>
    str.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

  _onChangeFilter = (type, value, fields) => {
    const where = { ...this.view.state.filters };

    ["basicTax", "apTax"].forEach((field) => delete where[field]);
    delete where["$or"];

    switch (type) {
      case "array":
        if (value && Array.isArray(fields)) {
          where["$or"] = fields.map((field) => ({
            [field]: {
              $elemMatch: { $regex: value, $options: "i" },
            },
          }));
        }
        break;
      default:
        const field = fields;
        if (value) {
          where[field] = { $regex: value, $options: "i" };
        } else {
          delete where[field]; // Remove filter if no value
        }
    }
    this.view.setState({ filters: where }, () => {
      this.filterSubmit(this.view.state.filters);
    });
  };
}

export default AnnualTaxesPresenter;
